"use client";

import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode
} from "react";
import { createBrowserSupabase } from "@/lib/supabase/browser";
import { subscribeLive } from "@/lib/supabase/liveChannel";
import { customerTopic } from "@/lib/realtimeTopics";
import {
  isResyncSuppressedPath,
  shouldSkipResync,
} from "@/lib/authResync";
import { withTimeout } from "@/lib/withTimeout";
import { brandDiscountsForGroup, withBrandDiscounts } from "@/lib/groupBrandDiscount";
import { fetchGroupBrandDiscountRules } from "@/lib/supabase/groupBrandDiscount";
import {
  forgetSessionBrandRules,
  loadDisplayedBrandRules
} from "@/lib/supabase/brandRulesSession";
import type { CustomerLevel } from "@/lib/orders";
import type { CustomerGroup, ResellerGroup } from "@/lib/reseller-groups";

export type CustomerStatus = "pending" | "active" | "inactive" | "rejected";

export type CustomerData = {
  id: string;
  fullName: string;
  storeName: string | null;
  phone: string;
  level: CustomerLevel;
  status: CustomerStatus;
  group: CustomerGroup;
};

type CustomerContextValue = {
  customer: CustomerData | null;
  isLoading: boolean;
  // true bila ADA session auth yang valid, terlepas dari ada/tidaknya baris
  // customer. Memungkinkan UI membedakan "tamu (belum login)" dari "sudah login
  // tapi bukan reseller" (mis. akun admin) — supaya admin tidak di-bounce ke
  // /login secara membingungkan.
  isAuthenticated: boolean;
  reload: () => void;
};

const CustomerContext = createContext<CustomerContextValue>({
  customer: null,
  isLoading: true,
  isAuthenticated: false,
  reload: () => {}
});

async function fetchCustomerData(userId: string) {
  const db = createBrowserSupabase("store");
  if (!db) return null;

  // withTimeout: fetch supabase-js tak punya timeout — request yang menggantung
  // (jaringan mobile putus-nyambung) membuat isLoading macet `true` selamanya →
  // halaman ber-gate customer jadi skeleton/blank permanen. Timeout melempar →
  // resolver mengembalikan `error` (state dipertahankan, loading dilepas).
  const { data, error } = await withTimeout(
    db
      .from("customers")
      .select(
        "id, full_name, store_name, phone, customer_level, status, group_id, reseller_groups(id, slot, name, discount_persen, is_active)"
      )
      .eq("auth_user_id", userId)
      .maybeSingle()
  );

  // PENTING: bedakan error transien (jaringan/RLS/timeout) dari "tidak ada baris".
  // Jangan kembalikan null saat query GAGAL — itu membuat pemanggil mengira
  // pelanggan tidak ada lalu meng-logout UI padahal session masih valid (akar
  // bug "sering terlogout"). Lempar error → biar pemanggil pertahankan state.
  if (error) throw error;
  if (!data) return null; // benar-benar tidak ada baris customer

  const raw = (data as any).reseller_groups as {
    id: string;
    slot: number;
    name: string;
    discount_persen: number;
    is_active: boolean;
  } | null;

  // Grup NONAKTIF = tanpa grup — cermin `groupFromRow` di jalur checkout
  // (`backfillUnitPrice.ts`). Sebelum ini katalog masih memberi diskon grup
  // yang sudah admin matikan (harga level + badge "Harga <Grup>"), sementara
  // checkout menagih harga umum: dua harga untuk barang yang sama, dan yang
  // ditagih lebih mahal dari yang dipajang. Diselaraskan ke sisi yang menagih.
  const group: CustomerGroup =
    raw && raw.is_active !== false
      ? {
          id: raw.id,
          slot: raw.slot,
          name: raw.name,
          discountPersen: Number(raw.discount_persen ?? 0),
          isActive: true
        }
      : null;

  // Diskon merek per level (mig 7070) — hanya bila pembeli ber-level aktif.
  // Gagal dimuat → aturan resolusi SEBELUMNYA untuk pelanggan & level yang
  // sama (bughunt D13-4: satu timeout saat resync dulu membuang harga merek dari
  // layar dan cadangan checkout); tanpa itu → grup TANPA peta aturan (harga
  // level biasa) + peringatan. TIDAK melempar: kegagalan ini tak boleh
  // meng-logout UI. Jalur uang memuat ulang aturan secara strict sendiri;
  // checkout memakai aturan sesi ini HANYA sebagai cadangan kedua
  // (`loadBrandRulesForCheckout`), jadi yang diingat = persis yang dipajang.
  let pricedGroup: CustomerGroup = group;
  if (!group) {
    forgetSessionBrandRules();
  } else {
    const rules = await loadDisplayedBrandRules(data.id as string, group.id, () =>
      withTimeout(fetchGroupBrandDiscountRules(db))
    );
    if (rules) pricedGroup = withBrandDiscounts(group, brandDiscountsForGroup(rules, group.id));
  }

  return {
    id: data.id as string,
    fullName: data.full_name as string,
    storeName: (data.store_name as string | null) ?? null,
    phone: data.phone as string,
    level: data.customer_level as CustomerLevel,
    status: data.status as CustomerStatus,
    group: pricedGroup
  } satisfies CustomerData;
}

/**
 * Hasil resolusi session+customer, dengan membedakan kondisi TRANSIEN dari
 * kondisi PASTI. Ini kunci agar UI tidak salah meng-logout:
 * - `ok`          : ada session + ada baris customer → tampilkan.
 * - `no-session`  : server pasti tidak ada session → logout UI wajar.
 * - `no-customer` : ada session tapi tidak ada baris customer (jarang) → null.
 * - `error`       : kegagalan transien (jaringan/RLS/timeout/refresh) →
 *                   JANGAN ubah state; pertahankan customer yang sekarang.
 */
type ResolveResult =
  | { status: "ok"; customer: CustomerData }
  | { status: "no-session" }
  | { status: "no-customer" }
  | { status: "error" };

async function resolveCurrentCustomer(): Promise<ResolveResult> {
  const db = createBrowserSupabase("store");
  if (!db) return { status: "no-session" };

  try {
    // withTimeout: getUser bisa menggantung (fetch tanpa timeout + perebutan
    // navigator.locks auth). TimeoutError → catch → status "error" (transien).
    const { data: { user }, error: userError } = await withTimeout(
      db.auth.getUser()
    );

    if (!user) {
      // Tanpa user: bedakan "tidak ada session" (logout sungguhan) dari kegagalan
      // jaringan transien. getUser tanpa session → AuthSessionMissingError (logout
      // wajar). Kegagalan fetch → AuthRetryableFetchError (jangan logout).
      if (userError && userError.name === "AuthRetryableFetchError") {
        return { status: "error" };
      }
      return { status: "no-session" };
    }

    const customer = await fetchCustomerData(user.id); // throw bila query gagal
    if (!customer) return { status: "no-customer" };
    return { status: "ok", customer };
  } catch {
    // fetchCustomerData / getUser melempar → anggap transien, pertahankan state.
    return { status: "error" };
  }
}

export function CustomerProvider({ children }: { children: ReactNode }) {
  const [customer, setCustomer] = useState<CustomerData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const reloadRef = useRef(0);
  // PF-07 (b): stempel resync terakhir — HANYA milik jalur visibility/focus.
  const lastResyncAtRef = useRef(0);
  // PF-07 (c): promise resolusi yang sedang berjalan, di-share HANYA antar
  // pemicu SETARA "baca keadaan sekarang" (visibility/focus). Jalur auth event
  // & realtime SELALU memulai resolusi BARU — keduanya menandakan keadaan
  // BARU BERUBAH, dan hasil yang mulai SEBELUM event bisa menutupi
  // logout/login/pembaruan sungguhan bila di-share.
  const inflightRef = useRef<Promise<ResolveResult> | null>(null);

  const resolveSharedCustomer = (): Promise<ResolveResult> => {
    if (inflightRef.current) return inflightRef.current;
    const p = resolveCurrentCustomer().finally(() => {
      if (inflightRef.current === p) inflightRef.current = null;
    });
    inflightRef.current = p;
    return p;
  };

  // Trigger reload dari luar tanpa menyentuh deps array
  const reload = () => { reloadRef.current += 1; };

  // Terapkan hasil resolusi ke state. Pada `error` (transien) JANGAN sentuh
  // customer — biarkan nilai lama bertahan agar UI tidak berkedip "logout".
  const applyResolution = (res: ResolveResult) => {
    if (res.status === "ok") {
      setCustomer(res.customer);
      setIsAuthenticated(true);
    } else if (res.status === "no-customer") {
      // Ada session tapi bukan reseller (mis. admin): authenticated, customer null.
      setCustomer(null);
      setIsAuthenticated(true);
    } else if (res.status === "no-session") {
      setCustomer(null);
      setIsAuthenticated(false);
    }
    // res.status === "error": pertahankan state saat ini (no-op)
  };

  useEffect(() => {
    let cancelled = false;

    async function load() {
      const db = createBrowserSupabase("store");
      if (!db) { setIsLoading(false); return; }

      const res = await resolveCurrentCustomer();
      if (!cancelled) {
        applyResolution(res);
        setIsLoading(false);
      }
    }

    void load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadRef.current]); // re-run saat reload() dipanggil

  // Auth state changes
  useEffect(() => {
    const db = createBrowserSupabase("store");
    if (!db) return;

    const { data: listener } = db.auth.onAuthStateChange((event, session) => {
      // PENTING: callback onAuthStateChange dijalankan auth-js SAAT inisialisasi
      // (event INITIAL_SESSION/TOKEN_REFRESHED) sambil MENUNGGU callback selesai.
      // Memanggil supabase (fetchCustomerData → query) langsung di sini akan
      // menunggu getSession → menunggu initializePromise yang BELUM selesai →
      // DEADLOCK: semua query supabase-js menggantung (picker brand/kategori
      // kosong, tombol "Buat ..." macet "Menyimpan"). Karena itu kerja supabase
      // ditunda keluar dari callback dengan setTimeout(0).
      //
      // PF-07 (c): event auth meng-INVALIDASI promise resolusi yang sedang
      // berjalan — resolusi yang mulai sebelum event membawa keadaan lama;
      // pemanggil visibility/focus berikutnya wajib mulai segar.
      inflightRef.current = null;
      if (event === "SIGNED_IN" || event === "TOKEN_REFRESHED" || event === "USER_UPDATED") {
        // Penting: pada TOKEN_REFRESHED (≈ tiap jam) JANGAN timpa customer dengan
        // null bila query customers gagal sesaat — itu meng-logout UI tanpa sebab.
        // applyResolution mempertahankan state saat hasil `error`.
        setTimeout(() => {
          void (async () => {
            const res = await resolveCurrentCustomer();
            applyResolution(res);
            setIsLoading(false);
          })();
        }, 0);
      } else if (event === "SIGNED_OUT") {
        // JANGAN langsung logout UI. supabase-js memancarkan SIGNED_OUT juga saat
        // auto-refresh gagal sesaat (balapan rotasi refresh-token middleware vs
        // browser, atau tab mobile bangun dari background). Verifikasi dulu ke
        // server: bila session sebenarnya masih valid, pertahankan/segarkan
        // customer (self-heal). Hanya null-kan saat server pasti tak ada session.
        setTimeout(() => {
          void (async () => {
            const res = await resolveCurrentCustomer();
            applyResolution(res);
            setIsLoading(false);
          })();
        }, 0);
      }
    });

    return () => listener.subscription.unsubscribe();
  }, []); // stabil — tidak berubah

  // Re-sync saat tab kembali aktif. Menangani kasus tab (terutama mobile) yang
  // lama di background: access-token kedaluwarsa, timer auto-refresh ter-throttle,
  // dan/atau cookie sudah disegarkan oleh middleware di tab lain. getUser membaca
  // cookie terbaru + memicu refresh bila perlu, lalu memulihkan state customer
  // tanpa perlu reload halaman penuh.
  useEffect(() => {
    let cancelled = false;

    const resync = () => {
      if (typeof document !== "undefined" && document.visibilityState !== "visible") {
        return;
      }
      // PF-07 — HANYA frekuensi jalur ini yang diatur; semantik self-heal &
      // diskriminasi error transien tak disentuh (jalur mount/auth/realtime
      // tetap tanpa guard ini):
      // (d) di panel admin/platform tak ada konsumen useCustomer (grep
      //     2026-08: nol di components/admin|platform; BottomNav/PopupDisplay
      //     self no-op) → resync sesi STORE di sana dilewati. Baca
      //     window.location langsung (bukan usePathname — handler event
      //     selalu segar tanpa menambah re-render provider). /toko TIDAK
      //     ikut senyap (ProductDetail katalog membaca isAuthenticated).
      if (
        typeof window !== "undefined" &&
        isResyncSuppressedPath(window.location.pathname)
      ) {
        return;
      }
      // (a)+(b) visible+focus menembak BERPASANGAN saat tab kembali → guard
      //     stempel meredamnya jadi SATU resolusi; kembali >15 dtk tetap
      //     resync. Stempel hanya digeser saat resolusi benar-benar
      //     dijadwalkan — event yang tersaring tak menggesernya (bolak-balik
      //     tab cepat tidak menunda resync selamanya).
      const now = Date.now();
      if (shouldSkipResync(lastResyncAtRef.current, now)) return;
      lastResyncAtRef.current = now;
      // Tunda keluar dari handler event (hindari deadlock init auth-js, lihat
      // catatan pada listener onAuthStateChange).
      setTimeout(() => {
        void (async () => {
          // (c) berbagi promise dgn pemicu setara yang sedang berjalan.
          const res = await resolveSharedCustomer();
          if (!cancelled) applyResolution(res);
        })();
      }, 0);
    };

    document.addEventListener("visibilitychange", resync);
    window.addEventListener("focus", resync);

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", resync);
      window.removeEventListener("focus", resync);
    };
  }, []);

  // Live: reload jika admin mengubah customer.
  //
  // ⚠️ Sejak mig 4440 ini BARU benar-benar menyala. Sebelumnya binding di sini
  // memakai `postgres_changes` pada tabel `customers`, padahal `customers`
  // TIDAK PERNAH masuk publication `supabase_realtime` — jadi selama ini
  // callback-nya tak pernah sekali pun terpanggil, tanpa error apa pun. Yang
  // menutupi lubangnya cuma resync visibility/focus di efek atas.
  useEffect(() => {
    if (!customer?.id) return;

    const db = createBrowserSupabase("store");
    if (!db) return;

    const id = customer.id;
    return subscribeLive(
      db,
      customerTopic(id),
      () => {
          // Pakai resolver ber-diskriminasi: jangan logout UI bila fetch gagal
          // transien saat admin meng-update data customer. SENGAJA tidak ikut
          // dedup PF-07 (c): event ini berarti barisnya BARU berubah — berbagi
          // promise yang mulai SEBELUM event bisa menyajikan data pra-update
          // dan pembaruan admin terlewat; resolusi di sini wajib segar, dan
          // promise berjalan DI-INVALIDASI (cermin auth event) supaya resync
          // visibility/focus sesudah ini juga tak menumpang hasil pra-update.
          inflightRef.current = null;
          void (async () => {
            const res = await resolveCurrentCustomer();
            applyResolution(res);
          })();
      },
      {
        // Cadangan bila channel gagal/ditolak: tarik ulang berkala, jangan
        // diam-diam menyajikan profil basi.
        onDegraded: () => {
          inflightRef.current = null;
          void (async () => {
            const res = await resolveCurrentCustomer();
            applyResolution(res);
          })();
        }
      }
    );
  }, [customer?.id]); // hanya re-subscribe saat id berubah

  return (
    <CustomerContext.Provider value={{ customer, isLoading, isAuthenticated, reload }}>
      {children}
    </CustomerContext.Provider>
  );
}

export function useCustomer() {
  return useContext(CustomerContext);
}

export function useCustomerGroup(): CustomerGroup {
  const { customer } = useContext(CustomerContext);
  return customer?.group ?? null;
}
